import * as Sentry from '@sentry/react';
import api from './api';

const isDev = import.meta.env.DEV;

interface LogEvent {
  level: 'info' | 'warn' | 'error';
  message: string;
  metadata?: Record<string, any>;
}

const offlineQueue: LogEvent[] = [];
const logThrottleMap = new Map<string, number>();

// Initialize Sentry
export function initializeErrorTracking() {
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE,
      tracesSampleRate: 0.2,
      integrations: [
        new Sentry.BrowserTracing({
          tracePropagationTargets: ['localhost', 'your-domain.com'],
        }),
      ],
    });
  }
}

// Performance monitoring
export function initializePerformanceMonitoring() {
  if ('performance' in window) {
    // Monitor page load performance
    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      logEvent({
        level: 'info',
        message: 'Page Load Performance',
        metadata: {
          loadTime: navigation.loadEventEnd - navigation.navigationStart,
          domInteractive: navigation.domInteractive - navigation.navigationStart,
          firstContentfulPaint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime,
        },
      }).catch(err => {
        if (isDev) {
          console.error('Failed to log performance metrics:', err);
        }
      });
    });

    // Monitor long tasks
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.duration > 50) { // Tasks longer than 50ms
          logEvent({
            level: 'warn',
            message: 'Long Task Detected',
            metadata: {
              duration: entry.duration,
              startTime: entry.startTime,
              name: entry.name,
            },
          }).catch(err => {
            if (isDev) {
              console.error('Failed to log long task:', err);
            }
          });
        }
      });
    });

    observer.observe({ entryTypes: ['longtask'] });
  }
}

let errorCount = 0;
let lastNotificationTime = 0;
const ERROR_THRESHOLD = 5;
const NOTIFICATION_COOLDOWN = 5 * 60 * 1000; // 5 minutes

// Process offline queue when back online
window.addEventListener('online', async () => {
  const events = [...offlineQueue];
  offlineQueue.length = 0;

  for (const event of events) {
    try {
      await logEvent(event);
    } catch (err) {
      offlineQueue.push(event);
      if (isDev) {
        console.warn('Failed to process offline event:', err);
      }
    }
  }
});

export async function logEvent({ level, message, metadata }: LogEvent) {
  try {
    // Store event for later if offline
    if (!navigator.onLine) {
      offlineQueue.push({ level, message, metadata });
      if (isDev) {
        console.log('Stored offline event:', { level, message, metadata });
      }
      return;
    }

    // Throttling: Prevent spamming the same log message
    const throttleKey = `${level}:${message}`;
    const lastLogTime = logThrottleMap.get(throttleKey);
    const now = Date.now();
    
    if (lastLogTime && now - lastLogTime < 2000) {
      return; // Skip if logged recently
    }
    logThrottleMap.set(throttleKey, now);

    // cleanup map if too large
    if (logThrottleMap.size > 100) {
      const oldestTime = now - 5000;
      for (const [key, time] of logThrottleMap.entries()) {
        if (time < oldestTime) logThrottleMap.delete(key);
      }
    }

    // Log to API
    await api.request('/api/logs', {
      method: 'POST',
      body: JSON.stringify({
        level,
        message,
        metadata,
        timestamp: new Date().toISOString()
      }),
    }).catch(() => {
      // Fall back to console logging if API is not available
      if (isDev) {
        console.log(`[${level.toUpperCase()}] ${message}`, metadata);
      }
    });

    if (level === 'error') {
      await handleError(message, metadata);
    }
  } catch (err) {
    // Store event for later if offline
    if (!navigator.onLine) {
      offlineQueue.push({ level, message, metadata });
      if (isDev) {
        console.log('Stored offline event:', { level, message, metadata });
      }
      return;
    }

    // Fall back to console logging in development
    if (isDev) {
      console.error('Failed to log event:', err);
      console.log(`[${level.toUpperCase()}] ${message}`, metadata);
    }

    // If in production, log to Sentry as a fallback
    if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureException(err);
    }
  }
}

async function handleError(message: string, metadata?: Record<string, any>) {
  errorCount++;

  const now = Date.now();
  if (errorCount >= ERROR_THRESHOLD && now - lastNotificationTime > NOTIFICATION_COOLDOWN) {
    try {
      await sendErrorNotification({
        subject: `[ALERT] High Error Rate Detected`,
        message: `Multiple errors have been detected in the system.\n\nLatest error: ${message}`,
        metadata
      });

      lastNotificationTime = now;
      errorCount = 0;
    } catch (err) {
      if (isDev) {
        console.error('Failed to send error notification:', err);
      }
      if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
        Sentry.captureException(err);
      }
    }
  }
}

interface ErrorNotification {
  subject: string;
  message: string;
  metadata?: Record<string, any>;
}

async function sendErrorNotification({ subject, message, metadata }: ErrorNotification) {
  if (isDev) {
    console.log('[Error Notification]', { subject, message, metadata });
    return;
  }

  await api.request('/api/notifications/error', {
    method: 'POST',
    body: JSON.stringify({
      subject,
      message,
      metadata,
      timestamp: new Date().toISOString()
    }),
  });
}
